import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useCurrentMedia, useCurrentRapidData } from "@/store";
import { useModelComparison } from "../contexts/ModelComparisonProvider";
import type { SourceInferenceResult } from "../types";
import { extractVersionFromRef } from "@/store/slices/modelSlice";
import { generateColorFromText } from "@/components/annotation/colors";
import { AnnotationCanvas } from "@/components/annotation/AnnotationCanvas";
import { BoundingBoxStrategyImpl, BoundingBox } from "@/utils/annotationStrategies/BoundingBox";
import { v4 as uuidv4 } from "uuid";
import { Chip } from "@scripts/react/components/Chip";
import { AnnotationLabelsToggle } from "@/components/flow/build/AnnotationLabelsToggle";
import { ZoomControls } from "@/components/flow/build/ZoomControls";

interface ZoomPanState {
    zoom: number;
    panX: number;
    panY: number;
}

const DEFAULT_ZOOM_STATE: ZoomPanState = { zoom: 1, panX: 0, panY: 0 };
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;

interface Prediction {
    x: number;
    y: number;
    width: number;
    height: number;
    class: string;
    confidence?: number;
    detection_id?: string;
}

/**
 * Extract predictions from inference result.
 * Handles both direct model inference format (predictions at top level)
 * and workflow inference format (predictions in outputs[0].predictions)
 */
function extractPredictions(result: any): Prediction[] {
    if (!result) return [];
    if (Array.isArray(result.predictions)) {
        return result.predictions;
    }
    if (result.outputs?.[0]?.predictions) {
        return result.outputs[0].predictions;
    }
    return [];
}

/**
 * Format confidence as a rounded percentage
 */
function formatConfidence(confidence: number | undefined): string {
    if (confidence === undefined) return "";
    return `${Math.round(confidence * 100)}%`;
}

/**
 * Convert predictions to BoundingBox format for AnnotationCanvas
 */
function predictionsToBoundingBoxes(predictions: Prediction[]): BoundingBox[] {
    return predictions.map((pred) => {
        const className = pred.class || "object";
        const confidenceStr = formatConfidence(pred.confidence);
        const label = confidenceStr ? `${className} ${confidenceStr}` : className;

        return {
            id: pred.detection_id || uuidv4(),
            x: pred.x,
            y: pred.y,
            width: pred.width,
            height: pred.height,
            label,
            color: generateColorFromText(className) // Use class name for consistent colors
        };
    });
}

/**
 * Loading overlay shown while inference is running
 */
function InferenceLoadingOverlay() {
    return (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="flex flex-col items-center gap-2">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/50 border-t-white" />
                <span className="text-sm text-white">Running inference...</span>
            </div>
        </div>
    );
}

const ZOOM_STEP = 0.25;

/**
 * Single model panel with image, predictions, and shared zoom/pan controls.
 * Mimics the fileCarouselWindow structure with fileCarouselHeader.
 * Uses AnnotationCanvas with externalAnnotations for consistent rendering.
 * Zoom/pan state and interaction handlers are controlled by parent for synchronized viewing.
 */
function ModelPanel({
    imageUrl,
    inference,
    label,
    imageWidth,
    imageHeight,
    isCurrent = false,
    zoomState,
    showLabels,
    setShowLabels,
    showControls = false,
    isPanning,
    onWheel,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    onZoomIn,
    onZoomOut,
    onResetZoom
}: {
    imageUrl: string;
    inference: SourceInferenceResult | undefined;
    label: string;
    imageWidth: number;
    imageHeight: number;
    /** Whether this is the current/new model (shows green glow) */
    isCurrent?: boolean;
    /** Shared zoom/pan state */
    zoomState: ZoomPanState;
    /** Shared label visibility state */
    showLabels: boolean;
    /** Setter for label visibility */
    setShowLabels: React.Dispatch<React.SetStateAction<boolean>>;
    /** Whether to show controls in this panel's header */
    showControls?: boolean;
    /** Whether user is currently panning (from parent) */
    isPanning: boolean;
    /** Wheel handler from parent */
    onWheel: (e: React.WheelEvent) => void;
    /** Mouse down handler from parent */
    onMouseDown: (e: React.MouseEvent) => void;
    /** Mouse move handler from parent */
    onMouseMove: (e: React.MouseEvent) => void;
    /** Mouse up handler from parent */
    onMouseUp: (e: React.MouseEvent) => void;
    /** Mouse leave handler from parent */
    onMouseLeave: (e: React.MouseEvent) => void;
    /** Zoom in handler from parent */
    onZoomIn: () => void;
    /** Zoom out handler from parent */
    onZoomOut: () => void;
    /** Reset zoom handler from parent */
    onResetZoom: () => void;
}) {
    const predictions = extractPredictions(inference?.result);
    const boundingBoxes = useMemo(() => predictionsToBoundingBoxes(predictions), [predictions]);
    const isLoading = !inference;
    const hasError = inference?.error;

    const [imageLoaded, setImageLoaded] = useState(false);
    // Track actual natural dimensions from the loaded image
    const [naturalDimensions, setNaturalDimensions] = useState<{
        width: number;
        height: number;
    } | null>(null);

    // Refs for AnnotationCanvas
    const containerRef = useRef<HTMLDivElement>(null);
    const mediaHolderRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    // Source dimensions for scaling external annotations - use natural image dimensions
    // These are the coordinates that predictions are relative to
    const sourceDimensions = useMemo(() => {
        if (naturalDimensions) {
            return naturalDimensions;
        }
        // Fallback to props if natural dimensions not yet available
        return { width: imageWidth, height: imageHeight };
    }, [naturalDimensions, imageWidth, imageHeight]);

    // Handle image load - capture natural dimensions
    const handleImageLoad = useCallback(() => {
        const img = imageRef.current;
        if (img) {
            setNaturalDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        }
        setImageLoaded(true);
    }, []);

    return (
        <div
            className={twClassNames(
                "flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white"
            )}
        >
            {/* Header - mimics fileCarouselHeader structure with AnnotationControls */}
            <div
                className={twClassNames(
                    "border-border flex h-12 flex-shrink-0 flex-row items-center justify-between gap-2 border-b bg-white px-3 py-2",
                    isCurrent ? "border-green-500/50" : "border-gray-200"
                )}
            >
                <div className="flex items-center gap-2">
                    <h3
                        className={twClassNames(
                            "text-sm font-medium",
                            isCurrent ? "text-green-600" : "text-primary"
                        )}
                    >
                        {label}
                    </h3>
                    {inference?.result && (
                        <Chip color={isCurrent ? "green" : "gray"}>
                            {predictions.length}
                            <i className="fas fa-vector-square" />
                        </Chip>
                    )}
                </div>
                {/* AnnotationControls - labels toggle and zoom controls - only shown when showControls is true */}
                {showControls && (
                    <div className="flex items-center gap-2">
                        <AnnotationLabelsToggle showLabels={showLabels} onToggle={setShowLabels} />
                        <div className="h-6 w-px flex-shrink-0 bg-divider" />
                        <ZoomControls
                            onZoomIn={onZoomIn}
                            onZoomOut={onZoomOut}
                            onResetZoom={onResetZoom}
                            canZoomIn={zoomState.zoom < MAX_ZOOM}
                            canZoomOut={zoomState.zoom > MIN_ZOOM}
                        />
                    </div>
                )}
            </div>

            {/* Image with predictions - zoomable/pannable container */}
            <div
                ref={containerRef}
                className="relative flex-1 cursor-grab overflow-hidden rounded-b-xl bg-gray-900 active:cursor-grabbing"
                onWheel={onWheel}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseLeave}
            >
                {/* Container for image and AnnotationCanvas overlay */}
                <div
                    ref={mediaHolderRef}
                    className="relative flex h-full w-full items-center justify-center"
                    style={{
                        transform: `translate(${zoomState.panX}px, ${zoomState.panY}px) scale(${zoomState.zoom})`,
                        transformOrigin: "center center",
                        transition: isPanning ? "none" : "transform 0.1s ease-out"
                    }}
                >
                    <img
                        ref={imageRef}
                        src={imageUrl}
                        alt={label}
                        className="block max-h-full max-w-full"
                        style={{ width: "auto", height: "auto" }}
                        draggable={false}
                        onLoad={handleImageLoad}
                    />
                    {imageLoaded && mediaHolderRef.current && imageRef.current && (
                        <AnnotationCanvas<BoundingBox>
                            strategy={BoundingBoxStrategyImpl}
                            mediaRef={imageRef}
                            containerRef={mediaHolderRef}
                            className="z-10"
                            forceHideLabels={!showLabels}
                            externalAnnotations={boundingBoxes}
                            externalSourceDimensions={sourceDimensions}
                        />
                    )}
                </div>
                {isLoading && <InferenceLoadingOverlay />}
                {hasError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <span className="text-sm text-red-300">Inference failed</span>
                    </div>
                )}
            </div>
        </div>
    );
}

interface ComparisonPreviewProps {
    /** Reference to the new/current model (required) */
    newModelRef?: string;
    /** Reference to the old/previous model (optional - if not provided, shows single model mode) */
    oldModelRef?: string;
    className?: string;
}

/**
 * Model preview component that supports both single model and side-by-side comparison modes.
 *
 * Single Model Mode:
 * - When only `newModelRef` is provided, shows a single panel with the model's predictions
 * - Panel is labeled with model name and takes full width
 *
 * Comparison Mode:
 * - When both `newModelRef` and `oldModelRef` are provided, shows two panels side-by-side

 *
 * Controls (labels toggle and zoom) are always shown in the left/current version header.
 * All interaction handlers are shared for smooth, lag-free synchronized viewing.
 */
export function ComparisonPreview({ newModelRef, oldModelRef, className }: ComparisonPreviewProps) {
    const currentMedia = useCurrentMedia();
    const { getInferencesForSource, isLoadingSources } = useModelComparison();
    const rapidData = useCurrentRapidData();
    const newVersion = extractVersionFromRef(newModelRef);
    const oldVersion = extractVersionFromRef(oldModelRef);

    // Shared zoom/pan state for both panels
    const [zoomState, setZoomState] = useState<ZoomPanState>(DEFAULT_ZOOM_STATE);
    const [showLabels, setShowLabels] = useState(true);

    // Shared panning refs for synchronized interaction
    const isPanningRef = useRef(false);
    const lastPanPositionRef = useRef({ x: 0, y: 0 });

    // Shared mouse event handlers for synchronized panning across both panels
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = -e.deltaY * 0.001;
        setZoomState((prev) => {
            const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom * (1 + delta)));
            return { ...prev, zoom: newZoom };
        });
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return; // Only left click
        isPanningRef.current = true;
        lastPanPositionRef.current = { x: e.clientX, y: e.clientY };
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isPanningRef.current) return;
        const dx = e.clientX - lastPanPositionRef.current.x;
        const dy = e.clientY - lastPanPositionRef.current.y;
        lastPanPositionRef.current = { x: e.clientX, y: e.clientY };
        setZoomState((prev) => ({ ...prev, panX: prev.panX + dx, panY: prev.panY + dy }));
    }, []);

    const handleMouseUp = useCallback(() => {
        isPanningRef.current = false;
    }, []);

    const handleMouseLeave = useCallback(() => {
        isPanningRef.current = false;
    }, []);

    const handleResetZoom = useCallback(() => {
        setZoomState(DEFAULT_ZOOM_STATE);
    }, []);

    const handleZoomIn = useCallback(() => {
        setZoomState((prev) => ({ ...prev, zoom: Math.min(MAX_ZOOM, prev.zoom + ZOOM_STEP) }));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoomState((prev) => ({ ...prev, zoom: Math.max(MIN_ZOOM, prev.zoom - ZOOM_STEP) }));
    }, []);

    // Get the image URL from the current media's file
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    useEffect(() => {
        const file = currentMedia?.file?.file;
        if (!file) {
            setImageUrl(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setImageUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [currentMedia?.file?.file]);

    // Get the source ID to look up inferences
    const sourceId = currentMedia?.file?.id;

    // Get inferences for this source
    const inferences = useMemo(
        () => (sourceId ? getInferencesForSource(sourceId) : undefined),
        [sourceId, getInferencesForSource]
    );

    // Get image dimensions
    const imageWidth = currentMedia?.file?.metadata?.naturalDimensions?.width || 640;
    const imageHeight = currentMedia?.file?.metadata?.naturalDimensions?.height || 640;

    // Loading state
    if (isLoadingSources || !imageUrl) {
        return (
            <div
                className={twClassNames(
                    "flex h-full items-center justify-center rounded-xl bg-gray-50",
                    className
                )}
            >
                <div className="flex flex-col items-center gap-3">
                    <div className="border-border h-8 w-8 animate-spin rounded-full border-4 border-t-brand" />
                    <span className="text-sm text-tertiary">Loading...</span>
                </div>
            </div>
        );
    }

    const isSingleModelMode = !oldModelRef;

    const oldInference = oldModelRef && inferences ? inferences[oldModelRef] : undefined;
    const newInference = newModelRef && inferences ? inferences[newModelRef] : undefined;

    // Error state
    if (!newModelRef) {
        return (
            <div
                className={twClassNames(
                    "flex h-full items-center justify-center bg-gray-50",
                    className
                )}
            >
                <div className="flex flex-col items-center gap-3">
                    <i className="fas fa-exclamation-triangle text-3xl text-orange-500" />
                    <span className="text-sm text-tertiary">No model selected</span>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            className={twClassNames(
                "flex min-h-0 flex-1 overflow-visible",
                isSingleModelMode ? "" : "gap-4",
                className
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
        >
            {/* Current version panel - full width in single model mode, left side in comparison mode */}
            <ModelPanel
                imageUrl={imageUrl}
                inference={newInference}
                label={
                    isSingleModelMode
                        ? rapidData?.workflowName || "Model Preview"
                        : newVersion
                          ? `Current Version (${newVersion})`
                          : "Current Version"
                }
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                isCurrent={!isSingleModelMode} // Only show green glow in comparison mode
                zoomState={zoomState}
                showLabels={showLabels}
                setShowLabels={setShowLabels}
                showControls={true} // Always show controls in the current/new model panel
                isPanning={isPanningRef.current}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onResetZoom={handleResetZoom}
            />

            {/* Previous version panel (right side) - only shown in comparison mode */}
            {!isSingleModelMode && (
                <ModelPanel
                    imageUrl={imageUrl}
                    inference={oldInference}
                    label={oldVersion ? `Previous Version (${oldVersion})` : "Previous Version"}
                    imageWidth={imageWidth}
                    imageHeight={imageHeight}
                    zoomState={zoomState}
                    showLabels={showLabels}
                    setShowLabels={setShowLabels}
                    showControls={false} // No controls in the previous version panel
                    isPanning={isPanningRef.current}
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onResetZoom={handleResetZoom}
                />
            )}
        </motion.div>
    );
}
